import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, Calendar, Mail, Phone, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReservationSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [reservation, setReservation] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/reservations/payment-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          // Optionnel : récupérer les détails de la réservation
          setReservation(response.data);
          return true;
        } else if (response.data.status === 'expired') {
          setPaymentStatus('expired');
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts >= maxAttempts) {
          setPaymentStatus('error');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const done = await checkPaymentStatus();
      if (!done && attempts < maxAttempts) {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [sessionId, navigate]);

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 px-4">
        <Card className="max-w-md w-full text-center shadow-2xl">
          <CardContent className="pt-12 pb-12">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-blue-600 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Vérification du paiement...</h2>
            <p className="text-gray-600">Veuillez patienter pendant que nous confirmons votre réservation.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 px-4 py-12">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardHeader className="text-center bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-xl py-8">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold">Réservation Confirmée !</CardTitle>
          </CardHeader>

          <CardContent className="pt-8 pb-8 space-y-6">
            <div className="text-center mb-8">
              <p className="text-xl text-gray-700 mb-4">
                Votre acompte de <span className="font-bold text-green-600">{reservation?.amount?.toFixed(2)}€</span> a été payé avec succès.
              </p>
              <p className="text-gray-600">
                Vous allez recevoir un email de confirmation à l'adresse indiquée avec tous les détails de votre réservation.
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-xl space-y-4">
              <h3 className="font-bold text-lg text-blue-900 mb-4">Prochaines étapes :</h3>
              
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <div className="font-semibold text-gray-900">Vérifiez vos emails</div>
                  <p className="text-sm text-gray-600">
                    Vous recevrez une confirmation avec votre numéro de réservation et tous les détails.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <div className="font-semibold text-gray-900">Solde restant</div>
                  <p className="text-sm text-gray-600">
                    Le solde sera à régler le jour de l'embarquement au Port de la Pointe Rouge.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <div className="font-semibold text-gray-900">Jour J</div>
                  <p className="text-sm text-gray-600">
                    Présentez-vous 15 minutes avant l'heure de départ. Bon vent ! ⛵
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-4">Besoin d'aide ?</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <a href="tel:0644731667" className="text-blue-600 hover:text-blue-800 font-medium">
                    06 44 73 16 67
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <a href="mailto:TheMyliBoat@gmail.com" className="text-blue-600 hover:text-blue-800 font-medium">
                    TheMyliBoat@gmail.com
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button 
                onClick={() => navigate('/')} 
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
              >
                Retour à l'accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-gray-50 px-4">
      <Card className="max-w-md w-full text-center shadow-2xl">
        <CardContent className="pt-12 pb-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Problème de paiement</h2>
          <p className="text-gray-600 mb-6">
            Nous n'avons pas pu confirmer votre paiement. Veuillez réessayer ou nous contacter.
          </p>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
              Retour à l'accueil
            </Button>
            <Button onClick={() => navigate('/#contact')} className="flex-1 bg-blue-600 hover:bg-blue-700">
              Nous contacter
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
